import React, { useState, useEffect, useCallback } from 'react';
import { Box, Paper, Grid, Skeleton, Alert, IconButton, Chip } from '@mui/material';
import {
  PieChart, Pie, Cell, Tooltip, ResponsiveContainer, Legend,
  BarChart, Bar, XAxis, YAxis, CartesianGrid,
  RadialBarChart, RadialBar,
} from 'recharts';
import {
  Users, CalendarCheck, PawPrint, Star, DollarSign,
  TrendingUp, UserPlus, Clock, CheckCircle2,
  RefreshCw,
} from 'lucide-react';
import api from '../config/api';

const ff = "'Poppins', sans-serif";

const COLORS = {
  blue: '#32A6D8', dark: '#005A64', green: '#3FA477',
  amber: '#E5A33D', red: '#E53E3E', purple: '#805AD5',
  pink: '#D53F8C', teal: '#319795',
};

// ─── Animated Counter ─────────────────────────────────────────
const AnimatedNumber = ({ value, prefix = '', suffix = '' }) => {
  const [display, setDisplay] = useState(0);
  useEffect(() => {
    if (!value) { setDisplay(0); return; }
    const target = Number(value);
    const duration = 1200;
    const steps = 40;
    const increment = target / steps;
    let current = 0;
    const timer = setInterval(() => {
      current += increment;
      if (current >= target) { setDisplay(target); clearInterval(timer); }
      else setDisplay(Math.floor(current));
    }, duration / steps);
    return () => clearInterval(timer);
  }, [value]);
  return <>{prefix}{display.toLocaleString()}{suffix}</>;
};

// ─── Stat Card ────────────────────────────────────────────────
const MetricCard = ({ label, value, icon, color, loading, prefix = '', suffix = '' }) => (
  <Paper elevation={0} sx={{
    p: 2.5, borderRadius: '14px', border: '1px solid #EDF2F7', height: '100%',
    transition: 'all 0.2s', '&:hover': { borderColor: color, boxShadow: `0 4px 20px ${color}15` },
  }}>
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
      <Box sx={{
        width: 38, height: 38, borderRadius: '10px', background: `${color}12`,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
      }}>
        {icon}
      </Box>
      <Box sx={{ fontFamily: ff, fontSize: 12, fontWeight: 500, color: '#A0AEC0' }}>{label}</Box>
    </Box>
    {loading ? (
      <Skeleton width={80} height={32} />
    ) : (
      <Box sx={{ fontFamily: ff, fontSize: 26, fontWeight: 700, color: '#1A202C' }}>
        <AnimatedNumber value={value} prefix={prefix} suffix={suffix} />
      </Box>
    )}
  </Paper>
);

// ─── Chart Card Wrapper ───────────────────────────────────────
const ChartCard = ({ title, subtitle, children, height = 300 }) => (
  <Paper elevation={0} sx={{
    p: 3, borderRadius: '14px', border: '1px solid #EDF2F7', height: '100%',
  }}>
    <Box sx={{ mb: 2 }}>
      <Box sx={{ fontFamily: ff, fontSize: 15, fontWeight: 600, color: '#1A202C' }}>{title}</Box>
      {subtitle && <Box sx={{ fontFamily: ff, fontSize: 11, color: '#A0AEC0', mt: 0.25 }}>{subtitle}</Box>}
    </Box>
    <Box sx={{ height }}>{children}</Box>
  </Paper>
);

// ─── Custom Tooltip ───────────────────────────────────────────
const CustomTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <Box sx={{
      background: 'white', border: '1px solid #EDF2F7', borderRadius: '8px',
      px: 2, py: 1.5, boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
    }}>
      <Box sx={{ fontFamily: ff, fontSize: 12, fontWeight: 600, color: '#2D3748', mb: 0.5 }}>{label}</Box>
      {payload.map((p, i) => (
        <Box key={i} sx={{ fontFamily: ff, fontSize: 11, color: p.color, display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <Box sx={{ width: 8, height: 8, borderRadius: '50%', background: p.color }} />
          {p.name}: {p.value?.toLocaleString()}
        </Box>
      ))}
    </Box>
  );
};

// ═══════ REPORTS ══════════════════════════════════════════════
export default function Reports() {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [refreshing, setRefreshing] = useState(false);

  const fetchStats = useCallback(async (isRefresh = false) => {
    try {
      if (isRefresh) setRefreshing(true); else setLoading(true);
      setError('');
      const res = await api.get('/admin/dashboard/stats');
      setStats(res.data.data || res.data);
    } catch (e) {
      if (!e?.silent) setError('Failed to load report data.');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  useEffect(() => { fetchStats(); }, [fetchStats]);

  // Auto-refresh every 30 seconds
  useEffect(() => {
    const interval = setInterval(() => fetchStats(true), 30000);
    return () => clearInterval(interval);
  }, [fetchStats]);

  // Derived chart data
  const bookingStatusData = stats?.bookingsByStatus
    ? [
        { name: 'Pending', value: stats.bookingsByStatus.pending || 0, color: COLORS.amber },
        { name: 'Confirmed', value: stats.bookingsByStatus.confirmed || 0, color: COLORS.blue },
        { name: 'Ongoing', value: stats.bookingsByStatus.ongoing || 0, color: COLORS.teal },
        { name: 'Completed', value: stats.bookingsByStatus.completed || 0, color: COLORS.green },
        { name: 'Cancelled', value: stats.bookingsByStatus.cancelled || 0, color: COLORS.red },
      ].filter(d => d.value > 0)
    : [];

  const sitterStatusData = stats?.sittersByStatus
    ? [
        { name: 'Approved', value: stats.sittersByStatus.approved || 0, color: COLORS.green },
        { name: 'Pending', value: stats.sittersByStatus.pending || 0, color: COLORS.amber },
        { name: 'Rejected', value: stats.sittersByStatus.rejected || 0, color: COLORS.red },
      ].filter(d => d.value > 0)
    : [];

  const platformHealthData = [
    { name: 'Users', value: stats?.totalUsers || 0, fill: COLORS.blue },
    { name: 'Bookings', value: stats?.totalBookings || 0, fill: COLORS.green },
    { name: 'Pets', value: stats?.totalPets || 0, fill: COLORS.amber },
    { name: 'Reviews', value: stats?.totalReviews || 0, fill: COLORS.purple },
  ];

  const radialData = [
    { name: 'Active', value: stats?.activeBookings || 0, fill: COLORS.green },
    { name: 'New Users', value: stats?.newUsersThisMonth || 0, fill: COLORS.blue },
    { name: 'New Bookings', value: stats?.newBookingsThisMonth || 0, fill: COLORS.amber },
  ];

  return (
    <Box>
      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}

      {/* Header with refresh + live indicator */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Chip
            icon={<Box sx={{
              width: 8, height: 8, borderRadius: '50%', background: '#38A169',
              animation: 'livePulse 2s ease-in-out infinite',
              '@keyframes livePulse': {
                '0%, 100%': { opacity: 1, boxShadow: '0 0 0 0 rgba(56,161,105,0.4)' },
                '50%': { opacity: 0.7, boxShadow: '0 0 0 6px rgba(56,161,105,0)' },
              },
            }} />}
            label="Live"
            size="small"
            sx={{
              fontFamily: ff, fontSize: 11, fontWeight: 600,
              background: '#F0FFF4', color: '#38A169', border: '1px solid #C6F6D5',
              '& .MuiChip-icon': { ml: 0.5 },
            }}
          />
          <Box sx={{ fontFamily: ff, fontSize: 12, color: '#A0AEC0' }}>
            Auto-refreshes every 30s
          </Box>
        </Box>
        <IconButton
          onClick={() => fetchStats(true)}
          disabled={refreshing}
          sx={{
            width: 36, height: 36, background: '#F7FAFC',
            '&:hover': { background: '#EDF2F7' },
            animation: refreshing ? 'spin 1s linear infinite' : 'none',
            '@keyframes spin': { '0%': { transform: 'rotate(0deg)' }, '100%': { transform: 'rotate(360deg)' } },
          }}
        >
          <RefreshCw size={16} color="#718096" />
        </IconButton>
      </Box>

      {/* ─── Key Metrics Row ──────────────────────────────────── */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        {[
          { label: 'Total Users', value: stats?.totalUsers, icon: <Users size={18} color={COLORS.blue} />, color: COLORS.blue },
          { label: 'Total Bookings', value: stats?.totalBookings, icon: <CalendarCheck size={18} color={COLORS.green} />, color: COLORS.green },
          { label: 'Total Pets', value: stats?.totalPets, icon: <PawPrint size={18} color={COLORS.amber} />, color: COLORS.amber },
          { label: 'Total Revenue', value: stats?.totalRevenue, icon: <DollarSign size={18} color={COLORS.dark} />, color: COLORS.dark, prefix: '$' },
          { label: 'Active Bookings', value: stats?.activeBookings, icon: <TrendingUp size={18} color={COLORS.purple} />, color: COLORS.purple },
          { label: 'Total Reviews', value: stats?.totalReviews, icon: <Star size={18} color={COLORS.pink} />, color: COLORS.pink },
        ].map((m, i) => (
          <Grid item xs={6} sm={4} md={2} key={i}>
            <MetricCard {...m} loading={loading} />
          </Grid>
        ))}
      </Grid>

      {/* ─── Growth Indicators ────────────────────────────────── */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        {[
          { label: 'New Users This Month', value: stats?.newUsersThisMonth, icon: <UserPlus size={18} color={COLORS.blue} />, color: COLORS.blue },
          { label: 'New Bookings This Month', value: stats?.newBookingsThisMonth, icon: <Clock size={18} color={COLORS.green} />, color: COLORS.green },
          { label: 'Approved Sitters', value: stats?.sittersByStatus?.approved, icon: <CheckCircle2 size={18} color={COLORS.green} />, color: COLORS.green },
          { label: 'Pending Sitters', value: stats?.sittersByStatus?.pending, icon: <Clock size={18} color={COLORS.amber} />, color: COLORS.amber },
        ].map((m, i) => (
          <Grid item xs={6} sm={3} key={i}>
            <MetricCard {...m} loading={loading} />
          </Grid>
        ))}
      </Grid>

      {/* ─── Charts Row 1 ─────────────────────────────────────── */}
      <Grid container spacing={2.5} sx={{ mb: 3 }}>
        {/* Bookings by Status - Donut */}
        <Grid item xs={12} md={4}>
          <ChartCard title="Bookings by Status" subtitle="Current distribution" height={280}>
            {loading ? (
              <Skeleton variant="circular" width={200} height={200} sx={{ mx: 'auto', mt: 2 }} />
            ) : bookingStatusData.length > 0 ? (
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={bookingStatusData}
                    cx="50%" cy="50%"
                    innerRadius={60} outerRadius={95}
                    dataKey="value"
                    animationBegin={0} animationDuration={1200}
                    stroke="none"
                    paddingAngle={3}
                  >
                    {bookingStatusData.map((d, i) => <Cell key={i} fill={d.color} />)}
                  </Pie>
                  <Tooltip content={<CustomTooltip />} />
                  <Legend
                    wrapperStyle={{ fontFamily: ff, fontSize: 11 }}
                    formatter={(val) => <span style={{ color: '#718096', fontSize: 11 }}>{val}</span>}
                  />
                </PieChart>
              </ResponsiveContainer>
            ) : (
              <Box sx={{ fontFamily: ff, fontSize: 12, color: '#A0AEC0', textAlign: 'center', pt: 8 }}>No booking data</Box>
            )}
          </ChartCard>
        </Grid>

        {/* Sitter Status - Donut */}
        <Grid item xs={12} md={4}>
          <ChartCard title="Sitter Approval" subtitle="Approval breakdown" height={280}>
            {loading ? (
              <Skeleton variant="circular" width={200} height={200} sx={{ mx: 'auto', mt: 2 }} />
            ) : sitterStatusData.length > 0 ? (
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={sitterStatusData}
                    cx="50%" cy="50%"
                    innerRadius={60} outerRadius={95}
                    dataKey="value"
                    animationBegin={200} animationDuration={1200}
                    stroke="none"
                    paddingAngle={3}
                  >
                    {sitterStatusData.map((d, i) => <Cell key={i} fill={d.color} />)}
                  </Pie>
                  <Tooltip content={<CustomTooltip />} />
                  <Legend
                    wrapperStyle={{ fontFamily: ff, fontSize: 11 }}
                    formatter={(val) => <span style={{ color: '#718096', fontSize: 11 }}>{val}</span>}
                  />
                </PieChart>
              </ResponsiveContainer>
            ) : (
              <Box sx={{ fontFamily: ff, fontSize: 12, color: '#A0AEC0', textAlign: 'center', pt: 8 }}>No sitter data</Box>
            )}
          </ChartCard>
        </Grid>

        {/* This Month Activity - Radial */}
        <Grid item xs={12} md={4}>
          <ChartCard title="This Month's Activity" subtitle="Key growth indicators" height={280}>
            {loading ? (
              <Skeleton variant="circular" width={200} height={200} sx={{ mx: 'auto', mt: 2 }} />
            ) : (
              <ResponsiveContainer width="100%" height="100%">
                <RadialBarChart
                  cx="50%" cy="50%"
                  innerRadius="30%" outerRadius="90%"
                  data={radialData}
                  startAngle={180} endAngle={-180}
                >
                  <RadialBar
                    dataKey="value"
                    cornerRadius={6}
                    animationBegin={400}
                    animationDuration={1200}
                  />
                  <Tooltip content={<CustomTooltip />} />
                  <Legend
                    wrapperStyle={{ fontFamily: ff, fontSize: 11 }}
                    formatter={(val) => <span style={{ color: '#718096', fontSize: 11 }}>{val}</span>}
                  />
                </RadialBarChart>
              </ResponsiveContainer>
            )}
          </ChartCard>
        </Grid>
      </Grid>

      {/* ─── Charts Row 2 ─────────────────────────────────────── */}
      <Grid container spacing={2.5} sx={{ mb: 3 }}>
        {/* Platform Overview - Bar Chart */}
        <Grid item xs={12} md={6}>
          <ChartCard title="Platform Overview" subtitle="Key entity counts" height={280}>
            {loading ? (
              <Skeleton variant="rounded" width="100%" height={240} />
            ) : (
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={platformHealthData} barSize={40}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#F0F0F0" />
                  <XAxis
                    dataKey="name" tick={{ fontFamily: ff, fontSize: 11, fill: '#718096' }}
                    axisLine={{ stroke: '#EDF2F7' }} tickLine={false}
                  />
                  <YAxis tick={{ fontFamily: ff, fontSize: 11, fill: '#A0AEC0' }} axisLine={false} tickLine={false} />
                  <Tooltip content={<CustomTooltip />} />
                  <Bar dataKey="value" radius={[6, 6, 0, 0]} animationBegin={0} animationDuration={1400}>
                    {platformHealthData.map((d, i) => <Cell key={i} fill={d.fill} />)}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            )}
          </ChartCard>
        </Grid>

        {/* User Breakdown - Area */}
        <Grid item xs={12} md={6}>
          <ChartCard title="User Composition" subtitle="Owners vs Sitters" height={280}>
            {loading ? (
              <Skeleton variant="rounded" width="100%" height={240} />
            ) : (
              <ResponsiveContainer width="100%" height="100%">
                <BarChart
                  data={[
                    { name: 'Pet Owners', count: stats?.totalOwners || 0 },
                    { name: 'Sitters', count: stats?.totalSitters || 0 },
                    { name: 'Admins', count: 1 },
                  ]}
                  barSize={50}
                  layout="vertical"
                >
                  <CartesianGrid strokeDasharray="3 3" stroke="#F0F0F0" horizontal={false} />
                  <XAxis type="number" tick={{ fontFamily: ff, fontSize: 11, fill: '#A0AEC0' }} axisLine={false} tickLine={false} />
                  <YAxis
                    type="category" dataKey="name"
                    tick={{ fontFamily: ff, fontSize: 12, fill: '#4A5568' }}
                    axisLine={false} tickLine={false} width={90}
                  />
                  <Tooltip content={<CustomTooltip />} />
                  <Bar dataKey="count" radius={[0, 6, 6, 0]} animationBegin={200} animationDuration={1400}>
                    <Cell fill={COLORS.blue} />
                    <Cell fill={COLORS.green} />
                    <Cell fill={COLORS.purple} />
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            )}
          </ChartCard>
        </Grid>
      </Grid>

      {/* Footer */}
      <Box sx={{
        pt: 2, borderTop: '1px solid #EDF2F7', display: 'flex',
        justifyContent: 'space-between', alignItems: 'center',
      }}>
        <Box sx={{ fontFamily: ff, fontSize: 11, color: '#CBD5E0' }}>OkTreat Admin - Live Platform Report</Box>
        <Box sx={{ fontFamily: ff, fontSize: 11, color: '#CBD5E0' }}>
          Last updated: {new Date().toLocaleString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
        </Box>
      </Box>
    </Box>
  );
}
